import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import theme, { TMaterialStyles } from '../../theme';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../enums';
import { getReferralStats } from '../../queries';
import { useReferralStore } from '../../stores';
import { ScreenBanner } from '../../components/Common/ScreenBanner';
import { useTranslation } from 'react-i18next';
import { ButtonWithShadow } from '../../components/Buttons/ButtonWithShadow';
import { Icon } from '../../components/Icon';
import _ from 'lodash';
import { Illustration } from '../../components/Illustration';
import { ReferralItem } from '../../components/Items/ReferralItem';
import { customToast } from '../../utils';
import WebApp from '@twa-dev/sdk';
import { TELEGRAM_BASE_SHARE_URL } from '../../api';

const ReferralScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { referralConfig, referralStats } = useReferralStore();

  const isEmptyReferrals = useMemo(
    () => _.isEmpty(referralStats?.referrals),
    [referralStats?.referrals],
  );

  const onBack = useCallback(() => navigate(ERoutes.main), [navigate]);

  const loadReferralStats = useCallback(async () => {
    try {
      await getReferralStats();
    } catch (error: any) {
      console.error('Error while [loadReferralStats]', error);
    }
  }, []);

  useEffect(() => {
    loadReferralStats();
  }, [loadReferralStats]);

  const onCopyReferralLink = useCallback(async () => {
    try {
      if (!referralConfig?.link) {
        return;
      }

      await navigator.clipboard.writeText(referralConfig.link);

      customToast({
        title: t('common.success'),
        subtitle: t('referrals.your_referral_link_copied'),
      });
    } catch (error: any) {
      console.error('Error while [onCopyReferralLink]', error);
    }
  }, [referralConfig?.link, t]);

  const onShareReferralLink = useCallback(() => {
    if (!referralConfig?.link) {
      return;
    }

    const stringShareUrl = `${TELEGRAM_BASE_SHARE_URL}?url=${referralConfig.link}&text=${t('referrals.play_and_guess_locations_together')}`;

    if (WebApp.platform === 'unknown') {
      window.open(stringShareUrl, '_blank');
      return;
    }

    WebApp.openTelegramLink(stringShareUrl);
  }, [referralConfig?.link, t]);

  return (
    <ScreenWrapper onBack={onBack}>
      <Box sx={materialStyles.screen}>
        <ScreenBanner
          illustrationName="InviteFriendsBanner"
          title={
            isEmptyReferrals
              ? t('referrals.invite_friends')
              : t('referrals.referrals')
          }
          topSubtitle={t('referrals.share_with_your_friends')}
        />
        {isEmptyReferrals ? (
          <Box sx={materialStyles.emptyReferralsContainer}>
            <Typography sx={materialStyles.emptyReferralsText}>
              {t('referrals.invite_to_get_more_rewards')}
            </Typography>
            <Box sx={materialStyles.emptyIllustrationContainer}>
              <Illustration
                sx={materialStyles.emptyIllustration}
                illustrationName="HippoWithPhone"
              />
            </Box>
          </Box>
        ) : null}
        {referralConfig?.link ? (
          <Box sx={materialStyles.inviteFriendsContainer}>
            <ButtonWithShadow
              onClick={onShareReferralLink}
              sx={materialStyles.inviteFriendsButton}
            >
              {t('referrals.invite_friends')}
            </ButtonWithShadow>
            <ButtonWithShadow
              onClick={onCopyReferralLink}
              sx={materialStyles.copyButton}
            >
              <Icon iconName="CopyIcon" sx={materialStyles.copyIcon} />
            </ButtonWithShadow>
          </Box>
        ) : null}
        {!isEmptyReferrals ? (
          <Box sx={materialStyles.referralCountContainer}>
            <Typography sx={materialStyles.referralsCountTitle}>
              {t('referrals.total')}
            </Typography>
            <Typography sx={materialStyles.referralsCountTitle}>
              {t('referrals.referrals_with_count', {
                value: referralStats?.referralCount,
              })}
            </Typography>
          </Box>
        ) : null}
        {!isEmptyReferrals ? (
          <Box sx={materialStyles.referralsListContainer}>
            {_.map(referralStats?.referrals, (referral) => (
              <ReferralItem referral={referral} />
            ))}
          </Box>
        ) : null}
      </Box>
    </ScreenWrapper>
  );
};

const materialStyles: TMaterialStyles = {
  screen: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    flex: 1,
    paddingBottom: '24px',
    maxWidth: '738px',
    width: '100%',
    mx: 'auto',
  },
  inviteFriendsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    marginTop: '16px',
    px: '18px',
  },
  inviteFriendsButton: {
    height: '51px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    borderRadius: '14px',
    fontSize: '12px',
    lineHeight: '100%',
    boxShadow: `0px 4px 0px ${theme.palette.secondary.contrastText}`,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    boxShadow: `0px 4px 0px ${theme.palette.secondary.contrastText}`,
    width: '47px',
    height: '51px',
    borderRadius: '14px',
  },
  copyIcon: {
    width: '20px',
    height: '20px',
  },
  referralCountContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: '15px',
    marginBottom: '10px',
    px: '18px',
  },
  referralsCountTitle: {
    textTransform: 'uppercase',
    fontSize: '21px',
    lineHeight: '100%',
    fontWeight: '600',
  },
  emptyReferralsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    px: '18px',
  },
  emptyReferralsText: {
    textTransform: 'uppercase',
    fontSize: '18px',
    lineHeight: '100%',
    fontWeight: '600',
    marginTop: '21px',
  },
  emptyIllustration: {
    width: '192px',
    height: '238px',
  },
  emptyIllustrationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginTop: '14px',
  },
  referralsListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
    px: '16px',
  },
};

export default ReferralScreen;
